import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dropdown, Tab, Nav } from "react-bootstrap";
import { RangeDatePicker } from "@y0c/react-datepicker";
import moment from "moment";
import { FiLayout, FiWifi } from "react-icons/fi";
import { FaMars, FaVenus } from "react-icons/fa";
import { dashBoard } from "../../../services/Auth/AuthService";
import {
  getGraphData,
  getOtpUser,
  getOnlineCount,
} from "../../../services/UserServices/UserService";
import { useDispatch } from "react-redux";
import {
  formFilledFilterPost,
  genderFilterPost,
  genderStatusFilterPost,
  setActiveFilterPage,
} from "../../../store/actions/FilterActions";
import CountryPicker from "../Modal/CountryPicker";
import DashboardStatCard from "./DashboardStatCard";
import "../../../css/dashboard.css";

const ONLINE_LOCATION_META = {
  singapore: { label: "Singapore", icon: "singapore" },
  malaysia: { label: "Malaysia", icon: "malaysia" },
  other: { label: "Other", icon: "other" },
  all: { label: "All", icon: "all" },
};

const Home = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [selectedCountry, setSelectedCountry] = useState("");
  const [type, setType] = useState("weekly");
  const [loader, setLoader] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [otpData, setOtpData] = useState(0);
  const [onlineData, setOnlineData] = useState(null);
  const [onlineLoader, setOnlineLoader] = useState(false);
  const [dashboardTab, setDashboardTab] = useState("overview");
  const [selectedOption, setSelectedOption] = useState("All");
  const [range, setRange] = useState({
    startDate: moment("2024-11-01"),
    endDate: moment("2024-11-10"),
  });

  async function getDashboardData() {
    let res;
    if (selectedOption === "All") {
      res = await dashBoard("", "", selectedCountry);
    } else {
      res = await dashBoard(
        moment(range.startDate).format("YYYY-MM-DD"),
        moment(range.endDate).format("YYYY-MM-DD"),
        selectedCountry
      );
    }
    setUsers(res.data?.data);
  }

  const handleFetchChart = async () => {
    setLoader(true);
    try {
      const response = await getGraphData(type);
      setChartData(response.data?.data);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoader(false);
    }
  };

  const otpHandler = async () => {
    try {
      const response = await getOtpUser();
      setOtpData(response.data?.data);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setLoader(false);
    }
  };

  const fetchOnlineCount = async () => {
    setOnlineLoader(true);
    try {
      const response = await getOnlineCount();
      setOnlineData(response.data?.data || null);
    } catch (error) {
      console.log(error, "error");
      setOnlineData(null);
    } finally {
      setOnlineLoader(false);
    }
  };

  useEffect(() => {
    getDashboardData();
  }, [selectedOption, range, selectedCountry]);

  useEffect(() => {
    handleFetchChart();
  }, [type]);

  const handleDashboardTabSelect = (tab) => {
    if (!tab) return;
    setDashboardTab(tab);
    if (tab === "online") {
      fetchOnlineCount();
    }
  };

  const renderOnlineStatCards = (title, counts, TitleIcon) => {
    if (!counts) return null;
    return (
      <>
        <div className="col-12">
          <h4 className="dashboard-section-title d-flex align-items-center gap-2">
            {TitleIcon ? <TitleIcon size={18} /> : null}
            {title}
          </h4>
        </div>
        {Object.keys(ONLINE_LOCATION_META).map((key) => (
          <DashboardStatCard
            key={`${title}-${key}`}
            title={ONLINE_LOCATION_META[key].label}
            value={counts?.[key] || 0}
            icon={ONLINE_LOCATION_META[key].icon}
            duration={0.8}
          />
        ))}
      </>
    );
  };

  const presetOptions = [
    "Today",
    "Yesterday",
    "Last 7 days",
    "Last 14 days",
    "Last 30 days",
    "This week",
    "Last week",
    "This month",
    "Last month",
    "All",
    "Custom",
  ];

  const handleSelectOption = (option) => {
    setSelectedOption(option);

    const today = moment();
    switch (option) {
      case "Today":
        setRange({ startDate: today, endDate: today });
        break;
      case "Yesterday": {
        const yesterday = moment().subtract(1, "days");
        setRange({ startDate: yesterday, endDate: yesterday });
        break;
      }
      case "Last 7 days":
        setRange({ startDate: moment().subtract(6, "days"), endDate: today });
        break;
      case "Last 14 days":
        setRange({ startDate: moment().subtract(13, "days"), endDate: today });
        break;
      case "This week":
        setRange({
          startDate: moment().startOf("week"),
          endDate: today,
        });
        break;
      case "Last week":
        setRange({
          startDate: moment().subtract(1, "week").startOf("week"),
          endDate: moment().subtract(1, "week").endOf("week"),
        });
        break;
      case "Last 30 days":
        setRange({ startDate: moment().subtract(29, "days"), endDate: today });
        break;
      case "This month":
        setRange({ startDate: moment().startOf("month"), endDate: today });
        break;
      case "Last month":
        setRange({
          startDate: moment().subtract(1, "month").startOf("month"),
          endDate: moment().subtract(1, "month").endOf("month"),
        });
        break;
      case "All":
        setRange({ startDate: moment("2000-01-01"), endDate: today });
        break;
      default:
        break;
    }
  };

  const overviewCards = [
    {
      title: "Get OTP",
      value: otpData || 0,
      icon: "otp",
      duration: 0.5,
      onClick: otpHandler,
    },
    {
      title: "Total Users",
      value: users?.userCount,
      icon: "users",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management");
        dispatch(genderFilterPost("all"));
      },
    },
    {
      title: "Total Males",
      value: users?.maleCount,
      icon: "male",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management");
        dispatch(genderFilterPost("male"));
      },
    },
    {
      title: "Total Active Males",
      value: users?.activeMale,
      icon: "activeMale",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management");
        dispatch(genderStatusFilterPost({ gender: "male", status: "enable" }));
      },
    },
    {
      title: "Total Females",
      value: users?.femaleCount,
      icon: "female",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management");
        dispatch(genderFilterPost("female"));
      },
    },
    {
      title: "Total Active Females",
      value: users?.activeFemale,
      icon: "activeFemale",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management");
        dispatch(
          genderStatusFilterPost({ gender: "female", status: "enable" })
        );
      },
    },
    {
      title: "Complete Users",
      value: users?.completeSignup,
      icon: "complete",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management");
        dispatch(formFilledFilterPost(true));
      },
    },
    {
      title: "Weekly Member Users",
      value: users?.weeklyPremiumUsers,
      icon: "weekly",
      onClick: () => {
        dispatch(setActiveFilterPage("members"));
        navigate("/members", { state: { type: "weekly" } });
      },
    },
    {
      title: "Monthly Member Users",
      value: users?.monthlyPremiumUsers,
      icon: "monthly",
      onClick: () => {
        dispatch(setActiveFilterPage("members"));
        navigate("/members", { state: { type: "monthly" } });
      },
    },
    {
      title: "3 Months Member Users",
      value: users?.quarterlyPremiumUsers,
      icon: "quarterly",
      onClick: () => {
        dispatch(setActiveFilterPage("members"));
        navigate("/members", { state: { type: "quarterly" } });
      },
    },
    {
      title: "6 Months Member Users",
      value: users?.semiAnnuallyPremiumUsers,
      icon: "semi",
      onClick: () => {
        dispatch(setActiveFilterPage("members"));
        navigate("/members", { state: { type: "semi_annually" } });
      },
    },
    {
      title: "Member By Admin",
      value: users?.premiumByAdmin,
      icon: "admin",
      onClick: () => {
        dispatch(setActiveFilterPage("members"));
        navigate("/members", { state: { type: "all" } });
      },
    },
    {
      title: "Total Member Users",
      value: users?.totalPremiumUsers,
      icon: "member",
      onClick: () => {
        dispatch(setActiveFilterPage("members"));
        navigate("/members", { state: { type: "all" } });
      },
    },
    {
      title: "Incomplete Users",
      value: users?.inCompleteSignup,
      icon: "incomplete",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management", {
          state: {
            gender: "male",
            selectedTab: "users",
            isFormFilled: false,
          },
        });
      },
    },
    {
      title: "Deleted Users",
      value: users?.deleteUser,
      icon: "deleted",
      onClick: () => {
        dispatch(setActiveFilterPage("users"));
        navigate("/user-management", {
          state: {
            gender: "female",
            selectedTab: "users",
            status: "delete",
          },
        });
      },
    },
  ];

  return (
    <div className="dashboard-shell default-tab">
      <Tab.Container
        activeKey={dashboardTab}
        onSelect={handleDashboardTabSelect}
      >
        <Nav as="ul" className="nav-tabs dashboard-tabs mb-3">
          <Nav.Item as="li">
            <Nav.Link eventKey="overview">
              <FiLayout size={15} />
              Overview
            </Nav.Link>
          </Nav.Item>
          <Nav.Item as="li">
            <Nav.Link eventKey="online">
              <FiWifi size={15} />
              Today's Online Users
            </Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="overview">
            <div className="form-head d-flex mb-3 justify-content-between align-items-start flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <Dropdown>
                  <Dropdown.Toggle
                    variant="outline-primary"
                    id="date-filter-dropdown"
                  >
                    {selectedOption}
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {presetOptions.map((option) => (
                      <Dropdown.Item
                        key={option}
                        onClick={() => handleSelectOption(option)}
                        active={selectedOption === option}
                      >
                        {option}
                      </Dropdown.Item>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>

                {selectedOption === "Custom" && (
                  <RangeDatePicker
                    startPlaceholder="From"
                    endPlaceholder="To"
                    startDate={range.startDate}
                    endDate={range.endDate}
                    onChange={(start, end) => {
                      setRange({
                        startDate: moment(start),
                        endDate: moment(end),
                      });
                    }}
                  />
                )}
              </div>
              <div className="col-6 col-md-4 col-lg-2 px-0">
                <CountryPicker
                  selectedCountry={selectedCountry}
                  setSelectedCountry={setSelectedCountry}
                  onSelect={(name) => setSelectedCountry(name)}
                />
              </div>
            </div>

            <div className="row">
              {overviewCards.map((card) => (
                <DashboardStatCard
                  key={card.title}
                  title={card.title}
                  value={card.value}
                  icon={card.icon}
                  onClick={card.onClick}
                  duration={card.duration ?? 1.5}
                />
              ))}
            </div>
          </Tab.Pane>

          <Tab.Pane eventKey="online">
            {onlineLoader ? (
              <div className="dashboard-loading">Loading online users...</div>
            ) : (
              <div className="row">
                {renderOnlineStatCards(
                  "Female Users",
                  onlineData?.femaleUsers,
                  FaVenus
                )}
                {renderOnlineStatCards(
                  "Male Users",
                  onlineData?.maleUsers,
                  FaMars
                )}
                {!onlineData && (
                  <div className="col-12">
                    <p className="dashboard-empty-state mb-0">
                      No online users data available.
                    </p>
                  </div>
                )}
              </div>
            )}
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
};

export default Home;
